import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Send, Bell, Users, History } from 'lucide-react';
import { format } from 'date-fns';
import { AdminLogger } from '@/lib/adminLogger';

type AppRole = 'admin' | 'landlord' | 'adjuster' | 'user' | 'client';

interface BroadcastHistory {
  id: string;
  title: string;
  message: string;
  target_roles: AppRole[] | null;
  sent_count: number;
  created_at: string;
}

const AVAILABLE_ROLES: { value: AppRole; label: string }[] = [
  { value: 'admin', label: 'Admins' },
  { value: 'landlord', label: 'Landlords' },
  { value: 'adjuster', label: 'Adjusters' },
  { value: 'client', label: 'Clients' },
  { value: 'user', label: 'Users' },
];

export default function AdminBroadcastNotifications() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<AppRole[]>([]);
  const [sendToAll, setSendToAll] = useState(true);
  const [history, setHistory] = useState<BroadcastHistory[]>([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('broadcast_notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRoleToggle = (role: AppRole) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSendBroadcast = async () => {
    if (!title.trim() || !message.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter both title and message.",
        variant: "destructive",
      });
      return;
    }

    if (!sendToAll && selectedRoles.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one role or choose 'Send to all users'.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-broadcast-notification', {
        body: {
          title: title.trim(),
          message: message.trim(),
          target_roles: sendToAll ? null : selectedRoles,
        },
      });

      if (error) throw error;

      // Log the broadcast
      await AdminLogger.broadcastNotification(
        data.broadcast_id || 'unknown',
        title.trim(),
        sendToAll ? null : selectedRoles,
        data.sent_count || 0
      );

      toast({
        title: "Broadcast Sent",
        description: `Successfully sent to ${data.sent_count} users.`,
      });

      // Reset form
      setTitle('');
      setMessage('');
      setSelectedRoles([]);
      setSendToAll(true);

      // Refresh history
      fetchHistory();
    } catch (error: any) {
      console.error('Error sending broadcast:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send broadcast. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Broadcast Notifications</h1>
        <p className="text-muted-foreground">Send push notifications to all users or specific groups</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Send Broadcast Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Broadcast
            </CardTitle>
            <CardDescription>
              Compose and send a notification to users
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Notification Title</Label>
              <Input
                id="title"
                placeholder="Enter notification title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Enter notification message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="mt-1 min-h-[100px]"
              />
            </div>

            <div className="space-y-3">
              <Label>Target Audience</Label>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="send-to-all"
                  checked={sendToAll}
                  onCheckedChange={(checked) => setSendToAll(checked as boolean)}
                />
                <label
                  htmlFor="send-to-all"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Send to all users
                </label>
              </div>

              {!sendToAll && (
                <div className="pl-6 space-y-2">
                  <p className="text-sm text-muted-foreground mb-2">Select specific roles:</p>
                  {AVAILABLE_ROLES.map((role) => (
                    <div key={role.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`role-${role.value}`}
                        checked={selectedRoles.includes(role.value)}
                        onCheckedChange={() => handleRoleToggle(role.value)}
                      />
                      <label
                        htmlFor={`role-${role.value}`}
                        className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {role.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button 
              onClick={handleSendBroadcast} 
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Bell className="mr-2 h-4 w-4" />
                  Send Broadcast
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Preview
            </CardTitle>
            <CardDescription>
              How the notification will appear
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg p-4 bg-muted/30">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Bell className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm">
                    {title || 'Notification Title'}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {message || 'Your notification message will appear here...'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Just now
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                <strong>Target:</strong>{' '}
                {sendToAll ? (
                  'All users'
                ) : selectedRoles.length > 0 ? (
                  selectedRoles.map(r => AVAILABLE_ROLES.find(ar => ar.value === r)?.label).join(', ')
                ) : (
                  'No roles selected'
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Broadcast History
          </CardTitle>
          <CardDescription>
            Previously sent broadcast notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              No broadcasts sent yet
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Sent To</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((broadcast) => (
                  <TableRow key={broadcast.id}>
                    <TableCell className="font-medium">{broadcast.title}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {broadcast.message}
                    </TableCell>
                    <TableCell>
                      {broadcast.target_roles ? (
                        <div className="flex gap-1 flex-wrap">
                          {broadcast.target_roles.map(role => (
                            <Badge key={role} variant="secondary" className="text-xs">
                              {role}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <Badge variant="outline">All users</Badge>
                      )}
                    </TableCell>
                    <TableCell>{broadcast.sent_count} users</TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(broadcast.created_at), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
